import { Building2, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { Membership } from "@/api/types";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const ALL = "__all__";

/**
 * Always visible for client users: shows the organisation you are in, and opens a menu to go to
 * its page (or switch organisation when you belong to several).
 */
export function OrgSwitcher({ memberships, current }: { memberships: Membership[]; current: string | null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  if (memberships.length === 0) return null;
  const active = memberships.find((m) => m.organisation_slug === current);
  const label = active?.organisation_name ?? t("common.allOrganisations");
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="max-w-full gap-2 px-3" aria-label={t("common.orgSwitcherLabel", { org: label })}>
          <Building2 aria-hidden="true" className="h-4 w-4 shrink-0" />
          <span className="truncate">{label}</span>
          <ChevronDown aria-hidden="true" className="h-4 w-4 shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuLabel>{t("common.orgSwitcher")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup value={active?.organisation_slug ?? ALL} onValueChange={(v) => navigate(v === ALL ? "/" : `/${v}`)}>
          {memberships.map((m) => (
            <DropdownMenuRadioItem key={m.organisation_id} value={m.organisation_slug}>
              {m.organisation_name}
            </DropdownMenuRadioItem>
          ))}
          {memberships.length > 1 ? <DropdownMenuRadioItem value={ALL}>{t("common.allOrganisations")}</DropdownMenuRadioItem> : null}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
